import { Component, Inject, Input } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SharedDeliveryLinesBaseComponent } from '@shared/src/components/route/delivery/lines/shared.delivery.lines.base.component';
import { FileUpDownloadController } from '@shared/src/controllers/fileupdownload/fileupdownload.controller';
import { HomeController } from '@shared/src/controllers/home/home.controller';
import { ShipmentDeliveryController } from '@shared/src/controllers/route/delivery/shipmentdelivery.controller';
import { ActionDto } from '@shared/src/dtos/maintenance/action/actionDto';
import { ActionsStripDto } from '@shared/src/dtos/maintenance/action/ActionsStripDto';
import { ShipmentDeliveryDto } from '@shared/src/dtos/shipment/delivery/ShipmentDeliveryDto';
import { ShipmentDeliveryLineDto } from '@shared/src/dtos/shipment/delivery/ShipmentDeliveryLineDto';
import { ShipmentDeliveryLinesSaveDto } from '@shared/src/dtos/shipment/delivery/ShipmentDeliveryLinesSaveDto';
import { PalletTypes } from '@shared/src/enums/PalletTypes';
import { ShipmentDeliveryTotalFieldTypes } from '@shared/src/enums/ShipmentDeliveryTotalFieldTypes';

/***
 * Albarà "teòric" d'un shipment. Comparteix tota la lògica d'edició de
 * línies (columnes, lots, alta/baixa, control de canvis) amb l'editor de
 * línies del CMR a través de SharedDeliveryLinesBaseComponent.
 *
 * ATENCIÓ: abans desava sol amb debounce a cada tecla; ara treballa amb
 * buffer + confirmar, com el diàleg del CMR. Res es persisteix fins que
 * l'usuari confirma.
 */
@Component({
  selector: 'shared-shipment-delivery-component',
  templateUrl: './shared.shipment.delivery.component.html',
  styleUrls: ['./shared.shipment.delivery.component.scss'],
})
export class SharedShipmentDeliveryComponent extends SharedDeliveryLinesBaseComponent<ShipmentDeliveryLineDto> {

  constructor(private shipmentDeliveryController: ShipmentDeliveryController,
    @Inject('HomeController') protected homeController: HomeController) {
    super();
    if (homeController.useIonic) this.useIonic = homeController.useIonic();
  }

  public actions: ActionsStripDto;
  public useIonic: boolean = false;

  @Input() public hideactions: boolean;
  @Input() public inECMR: boolean;

  public __shipmentId: number;
  @Input()
  set shipmentId(value: number) {
    if (this.__shipmentId === value)
      return;
    this.__shipmentId = value;
    this.loadShipmentDeliveryData();
  }
  get shipmentId(): number {
    return this.__shipmentId;
  }

  public shipmentDelivery: ShipmentDeliveryDto;

  public loadShipmentDeliveryData() {
    if (this.__shipmentId == null || this.__shipmentId == 0) {
      this.loadData(null);
      return;
    }
    this.shipmentDeliveryController.getShipmentDelivery(this.__shipmentId, true).subscribe(data => {
      this.loadData(data);
    });
  }

  public loadData(data: ShipmentDeliveryDto) {
    this.shipmentDelivery = data;
    this.loadLines(data != null && data.lines != null ? data.lines : []);
    this.initTotalsState({
      quantity: data != null ? data.totalQuantityIsFixed : false,
      price: data != null ? data.totalPriceIsFixed : false,
      kg: data != null ? data.totalKgIsFixed : false,
      m3: data != null ? data.totalM3IsFixed : false,
      pallet: data != null ? data.totalPalletIsFixed : false,
      netKg: data != null ? data.totalNetKgIsFixed : false,
      boxes: data != null ? data.totalBoxesIsFixed : false,
      bags: data != null ? data.totalBagsIsFixed : false,
      sacks: data != null ? data.totalSacksIsFixed : false,
      litres: data != null ? data.totalLitresIsFixed : false,
    }, {
      quantity: data != null ? data.totalQuantityFixed : null,
      price: data != null ? data.totalPriceFixed : null,
      kg: data != null ? data.totalKgFixed : null,
      m3: data != null ? data.totalM3Fixed : null,
      pallet: data != null ? data.totalPalletFixed : null,
      netKg: data != null ? data.totalNetKgFixed : null,
      boxes: data != null ? data.totalBoxesFixed : null,
      bags: data != null ? data.totalBagsFixed : null,
      sacks: data != null ? data.totalSacksFixed : null,
      litres: data != null ? data.totalLitresFixed : null,
    });

    if (data != null && data.actions != null)
      this.serverActions = ActionsStripDto.buildFromActionsStripDto(data.actions);
    else
      this.serverActions = new ActionsStripDto();

    this.refreshActions();
  }

  /*** Les que arriben del back (recalcular, PDF, nova línia...) */
  private serverActions: ActionsStripDto = new ActionsStripDto();

  public static readonly ACTIONEDIT: string = "SHIPMENTDELIVERY.EDIT";
  public static readonly ACTIONSAVE: string = "SHIPMENTDELIVERY.SAVE";
  public static readonly ACTIONCANCEL: string = "SHIPMENTDELIVERY.CANCEL";

  /*** Nova línia i recalcular només tenen sentit dins de mode edició */
  private static readonly EDITMODE_ONLY_ACTIONS: string[] = ["SHIPMENTDELIVERY.NEWLINE", "SHIPMENTDELIVERY.RECALCULATE"];

  /***
   * Refà l'strip: les accions del back més les d'edició. Desar/cancel·lar només
   * hi surten quan hi ha canvis pendents, i sempre al mateix lloc que la resta,
   * no en una barra al peu.
   */
  private refreshActions() {
    let result = new ActionsStripDto();

    // Cal recórrer bar i menú per separat: listAsBar només conté les que es
    // mostren a la barra (showIn>=0), i deixar-ne alguna fora (com el PDF,
    // que sol anar només al menú) la feia desaparèixer per complet.
    let addServerAction = (action: ActionDto) => {
      if (SharedShipmentDeliveryComponent.EDITMODE_ONLY_ACTIONS.indexOf(action.actionId) >= 0 && !this.editMode)
        return;
      if(this.inECMR && action.actionId == "SHIPMENTDELIVERY.PDF")
        return;
      result.add(action);
    };
    if (this.serverActions != null) {
      if (this.serverActions.listAsBar != null)
        for (let action of this.serverActions.listAsBar)
          addServerAction(action);
      if (this.serverActions.listAsMenu != null)
        for (let action of this.serverActions.listAsMenu)
          addServerAction(action);
    }

    if (this.canModify) {
      // Amb canvis pendents ja hi ha desar/cancel·lar per resoldre'ls: mostrar
      // també "acabar edició" no té sentit (haurien de decidir primer què fan
      // amb la transacció oberta). Sense canvis pendents, el toggle Editar /
      // Acabar edició és l'únic que cal.
      if (!this.editMode)
        result.add(ActionDto.build(SharedShipmentDeliveryComponent.ACTIONEDIT, 'DELIVERYLINE.EDITMODE.BUTTON',
          'DELIVERYLINE.EDITMODE.BUTTON', 'fa-pencil', 5, 0, 1, ''));
      else if (!this.dirty)
        result.add(ActionDto.build(SharedShipmentDeliveryComponent.ACTIONEDIT, 'DELIVERYLINE.VIEWMODE.BUTTON',
          'DELIVERYLINE.VIEWMODE.BUTTON', 'fa-pencil', 5, 0, 1, ''));

      if (this.dirty) {
        result.add(ActionDto.build(SharedShipmentDeliveryComponent.ACTIONSAVE, 'MAINTENANCE.ACCEPT',
          'MAINTENANCE.ACCEPT', 'fa-check', 1, 0, 1, '').default());
        result.add(ActionDto.build(SharedShipmentDeliveryComponent.ACTIONCANCEL, 'CANCEL.BUTTON',
          'CANCEL.BUTTON', 'fa-times', 2, 0, 1, ''));
      }
    }

    this.actions = result;
  }

  /*** La base avisa a cada canvi de l'estat "hi ha canvis pendents" */
  public onDirtyChanged() {
    this.refreshActions();
  }

  public get canModify(): boolean {
    return this.shipmentDelivery != null && this.shipmentDelivery.canModify && !this.readonly;
  }

  /***
   * Camps que aquest editor (l'albarà "teòric" del shipment) sap pintar.
   * Un camp que existeixi a IDeliveryLine però no sigui en aquesta llista
   * queda amagat SEMPRE, sense error (veure el comentari gran a
   * DeliveryLineColumns.ts): si afegeixes una propietat nova a la línia i
   * no surt aquí, revisa primer aquesta llista.
   */
  protected supportedColumns(): string[] {
    return ['reference', 'description', 'quantity', 'units', 'kg', 'netKg', 'boxes', 'bags', 'sacks', 'litres',
      'wDimension', 'yDimension', 'hDimension', 'm3', 'palletCount', 'palletType', 'unitPrice', 'totalPrice',
      'packageId', 'userTags', 'publicTags', 'privateTags', 'batchInfo'];
  }

  /*** Els paquets seleccionables són només els d'aquest shipment */
  public get packageFixedFilterKeys(): Array<string> {
    return this.__shipmentId != null ? ["S:" + this.__shipmentId] : [];
  }

  // ///////////////////////////////////////// Totals i fixat manual

  public readonly totalFieldTypes = ShipmentDeliveryTotalFieldTypes;

  /***
   * Tots es veuen ja a la capçalera del peu, sense kg: kg es pinta a part, al
   * final de tot (just abans del chevron), igual que fa shared-delivery-line
   * amb la seva columna kg al "first-line".
   */
  public readonly primaryTotalFields: ShipmentDeliveryTotalFieldTypes[] = [
    ShipmentDeliveryTotalFieldTypes.quantity,
    ShipmentDeliveryTotalFieldTypes.boxes,
    ShipmentDeliveryTotalFieldTypes.bags,
    ShipmentDeliveryTotalFieldTypes.sacks,
    ShipmentDeliveryTotalFieldTypes.litres,
    ShipmentDeliveryTotalFieldTypes.m3,
    ShipmentDeliveryTotalFieldTypes.price,
    ShipmentDeliveryTotalFieldTypes.pallet,
    ShipmentDeliveryTotalFieldTypes.netKg,
    ShipmentDeliveryTotalFieldTypes.kg
  ];

  /*** Igual que el chevron de les línies: el peu de totals es plega/desplega */
  public totalsExpanded: boolean = false;

  // totalKg()/totalNetKg()/totalBoxes()/totalBags()/totalSacks()/totalLitres()/totalM3()/getComputedTotal()
  // ja venen de SharedDeliveryLinesBaseComponent (compartits amb el diàleg del CMR)

  /*** Etiqueta del total (reaprofitem les de la columna de línia, ja traduïdes) */
  public getTotalCaption(field: ShipmentDeliveryTotalFieldTypes): string {
    switch (field) {
      case ShipmentDeliveryTotalFieldTypes.quantity: return 'SHIPMENTDELIVERY.TOTALQUANTITY.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.price: return 'SHIPMENTDELIVERY.TOTALPRICE.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.m3: return 'SHIPMENTDELIVERY.TOTALM3.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.pallet: return 'SHIPMENTDELIVERY.TOTALPALLET.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.netKg: return 'DELIVERYLINE.NETKG.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.kg: return 'DELIVERYLINE.KG.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.boxes: return 'DELIVERYLINE.BOXES.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.bags: return 'DELIVERYLINE.BAGS.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.sacks: return 'DELIVERYLINE.SACKS.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.litres: return 'DELIVERYLINE.LITRES.CAPTION';
    }
  }

  // getIsFixed()/getFixedValue()/onToggleFixed()/onFixedValueChanged() ja venen de
  // SharedDeliveryLinesBaseComponent (compartits amb el diàleg del CMR); aquí només
  // calia inicialitzar-los amb els IsFixed/Fixed reals que arriben del servidor
  // (veure loadData()).

  protected buildEmptyLine(): ShipmentDeliveryLineDto {
    let line = new ShipmentDeliveryLineDto();
    line.shipmentId = this.__shipmentId;
    line.shipmentDeliveryId = this.shipmentDelivery != null ? this.shipmentDelivery.shipmentDeliveryId : null;
    line.line = this.lines.length + 1;
    line.quantity = 0;
    line.kg = 0;
    line.netKg = 0;
    line.wDimension = 0;
    line.yDimension = 0;
    line.hDimension = 0;
    line.m3 = 0;
    line.palletCount = 0;
    line.palletType = PalletTypes.None;
    line.unitPrice = 0;
    line.totalPrice = 0;
    line.litres = 0;
    line.bags = 0;
    line.boxes = 0;
    line.sacks = 0;
    return line;
  }

  protected persist(lines: ShipmentDeliveryLineDto[]): Observable<any> {
    let data = new ShipmentDeliveryLinesSaveDto();
    data.shipmentId = this.__shipmentId;
    data.shipmentDeliveryId = this.shipmentDelivery != null ? this.shipmentDelivery.shipmentDeliveryId : null;
    data.lines = lines;

    data.totalQuantityIsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.quantity);
    data.totalQuantityFixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.quantity);
    data.totalPriceIsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.price);
    data.totalPriceFixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.price);
    data.totalKgIsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.kg);
    data.totalKgFixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.kg);
    data.totalM3IsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.m3);
    data.totalM3Fixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.m3);
    data.totalPalletIsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.pallet);
    data.totalPalletFixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.pallet);
    data.totalNetKgIsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.netKg);
    data.totalNetKgFixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.netKg);
    data.totalBoxesIsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.boxes);
    data.totalBoxesFixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.boxes);
    data.totalBagsIsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.bags);
    data.totalBagsFixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.bags);
    data.totalSacksIsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.sacks);
    data.totalSacksFixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.sacks);
    data.totalLitresIsFixed = this.getIsFixed(ShipmentDeliveryTotalFieldTypes.litres);
    data.totalLitresFixed = this.getTotalValueToSend(ShipmentDeliveryTotalFieldTypes.litres);

    return this.shipmentDeliveryController.saveShipmentDeliveryLines(data)
      .pipe(map(result => {
        if (result != null)
          this.loadData(result);
        return result;
      }));
  }

  protected reloadAfterCancel(): void {
    this.loadShipmentDeliveryData();
  }

  onClickAction(action: ActionDto) {
    if (this.shipmentDelivery == null || action == null)
      return;

    if (action.actionId == "SHIPMENTDELIVERY.RECALCULATE")
      this.shipmentDeliveryController.recalculate(this.shipmentDelivery.shipmentDeliveryId).subscribe(data => {
        this.loadData(data);
      });

    if (action.actionId == "SHIPMENTDELIVERY.PDF")
      this.shipmentDeliveryController.printpdf(this.shipmentDelivery.shipmentDeliveryId).subscribe(data => {
        FileUpDownloadController.resultDownLoad(data);
      });

    if (action.actionId == "SHIPMENTDELIVERY.NEWLINE") {
      this.editMode = true;
      this.onAddLine();
      this.refreshActions();
    }

    if (action.actionId == SharedShipmentDeliveryComponent.ACTIONEDIT) {
      this.editMode = !this.editMode;
      this.refreshActions();
    }

    if (action.actionId == SharedShipmentDeliveryComponent.ACTIONSAVE)
      this.onConfirm();

    if (action.actionId == SharedShipmentDeliveryComponent.ACTIONCANCEL)
      this.onCancel();
  }

  ngOnInit() {
    // Desar/cancel·lar apareixen i desapareixen segons si hi ha canvis pendents
    this.dirtyChanged.subscribe(() => this.refreshActions());
  }

}
